import {
	AddIcon,
	Button,
	CopyIcon,
	DeleteIcon,
	Modal,
	ModuleHeader,
	PieChartIcon,
	SearchIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useModulePermission } from '../../hooks/useModulePermission'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import type { EnpsDashboardStore } from './stores/enpsDashboardStore'
import type { EnpsConfigListItem, SearchEnpsConfigsInput } from './types'

interface EnpsPageProps {
	store: () => EnpsDashboardStore
}

type SearchParams = Omit<SearchEnpsConfigsInput, 'organizationId'>

export const EnpsPage = ({ store }: EnpsPageProps) => {
	const {
		enpsConfigs,
		selectedConfig,
		isLoading,
		error,
		searchQuery,
		initialize,
		search,
		duplicateSelectedConfig,
		deleteSelectedConfig,
		setSelectedConfig,
		setSearchQuery,
	} = store()

	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const toast = useToast()
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	// Check eNPS module permission
	const { isLoading: isCheckingPermission, isAuthorized, role } = useModulePermission('enps')
	const isViewerAdminOnly = role === 'viewerAdmin'

	// Redirect unauthorized users
	useEffect(() => {
		if (!isCheckingPermission && !isAuthorized) {
			toast.showToast('You do not have permission to access the eNPS module', 'error')
			navigate('/')
		}
	}, [isCheckingPermission, isAuthorized, navigate, toast])

	useEffect(() => {
		if (organizationId) {
			initialize(organizationId)
		}
	}, [organizationId, initialize])

	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
		}
	}, [error, toast])

	const handleOpenDuplicateModal = (config: EnpsConfigListItem) => {
		setSelectedConfig(config)
		setIsDuplicateModalOpen(true)
	}

	const handleOpenDeleteModal = (config: EnpsConfigListItem) => {
		setSelectedConfig(config)
		setIsDeleteModalOpen(true)
	}

	const handleDuplicate = async () => {
		try {
			await duplicateSelectedConfig()
			toast.showToast('NPS configuration duplicated successfully')
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDuplicateModalOpen(false)
		}
	}

	const handleDelete = async () => {
		try {
			await deleteSelectedConfig()
			toast.showToast('NPS configuration deleted successfully')
		} catch {
			// Error handling is done in the store
		} finally {
			setIsDeleteModalOpen(false)
		}
	}

	const handleRowClick = (config: EnpsConfigListItem) => {
		navigate(`/enps/${config.id}/general`)
	}

	// Handle external search changes with debouncing
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value)

			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}

			searchTimeoutRef.current = setTimeout(() => {
				if (organizationId) {
					search({ organizationId })
				}
			}, 300)
		},
		[organizationId, setSearchQuery, search],
	)

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	// Query options generator for GenericTable
	const queryOptionsGenerator = useCallback((_options: SearchParams) => {
		return { enabled: false }
	}, [])

	// Create conditional empty state based on search context
	const getEmptyState = useMemo(() => {
		if (searchQuery.trim().length > 0) {
			return {
				icon: SearchIcon,
				title: 'No NPS surveys match your search',
				description: 'Try adjusting your search terms or clear the search to see all surveys',
			}
		}

		return {
			icon: PieChartIcon,
			title: 'No NPS surveys created yet',
			description: 'Get started by creating your first NPS survey',
			actionLabel: 'Create NPS Survey',
			onAction: () => navigate('/enps/create/general'),
		}
	}, [searchQuery, navigate])

	// Show spinner while checking permissions or loading data
	if (isCheckingPermission || isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	// Don't render if not authorized (redirect will happen via useEffect)
	if (!isAuthorized) {
		return null
	}

	const formatStatus = (status: string) => {
		return status.charAt(0).toUpperCase() + status.slice(1)
	}

	const formatCadence = (cadence: string) => {
		return cadence.charAt(0).toUpperCase() + cadence.slice(1)
	}

	const columns: Column<EnpsConfigListItem>[] = [
		{
			field: 'name',
			label: 'Name',
			colSpan: 2,
			sortable: true,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{config.name}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			sortable: true,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{formatStatus(config.status)}
				</Text>
			),
		},
		{
			field: 'cadence',
			label: 'Cadence',
			colSpan: 1,
			sortable: true,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{formatCadence(config.cadence)}
				</Text>
			),
		},
		{
			field: 'nextScheduledDate',
			label: 'Next Survey',
			colSpan: 1,
			sortable: true,
			render: (config: EnpsConfigListItem) => {
				const isValidDate = config.nextScheduledDate
				return (
					<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
						{isValidDate ? new Date(isValidDate).toLocaleDateString() : '-'}
					</Text>
				)
			},
		},
		{
			field: 'executionCount',
			label: 'Surveys Sent',
			colSpan: 1,
			sortable: false,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{config.executionCount || 0}
				</Text>
			),
		},
		{
			field: 'responseCount',
			label: 'Responses',
			colSpan: 1,
			sortable: false,
			render: (config: EnpsConfigListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{config.responseCount || 0}
				</Text>
			),
		},
		...(!isViewerAdminOnly
			? [
					{
						field: 'id' as keyof EnpsConfigListItem,
						label: 'Actions',
						colSpan: 1,
						sortable: false,
						render: (config: EnpsConfigListItem) => (
							// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
							<div
								role='presentation'
								className='flex justify-start gap-2'
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}>
								<Button
									variant='secondary'
									size='small'
									onClick={(e) => {
										e.stopPropagation()
										handleOpenDuplicateModal(config)
									}}
									title='Duplicate survey'>
									<CopyIcon />
								</Button>
								<Button
									variant='secondary'
									size='small'
									onClick={(e) => {
										e.stopPropagation()
										handleOpenDeleteModal(config)
									}}
									title='Delete survey'>
									<DeleteIcon />
								</Button>
							</div>
						),
					},
				]
			: []),
	]

	return (
		<main className='p-8 pl-12'>
			<ModuleHeader breadcrumb={[{ label: 'Home', to: '/' }, { label: 'NPS' }]} title='NPS Surveys' />

			<GenericTable<EnpsConfigListItem, SearchParams>
				data={enpsConfigs}
				columns={columns}
				onRowClick={handleRowClick}
				externalDataFetching={true}
				searchValue={searchQuery}
				onSearchChange={handleSearchChange}
				queryOptionsGenerator={queryOptionsGenerator}
				actionButtons={
					isViewerAdminOnly
						? []
						: [
								{
									children: 'Create NPS Survey',
									icon: AddIcon,
									onClick: () => navigate('/enps/create/general'),
									variant: 'primary',
								},
							]
				}
				emptyState={getEmptyState}
			/>

			{/* Duplicate Modal */}
			<Modal
				isOpen={isDuplicateModalOpen}
				onClose={() => setIsDuplicateModalOpen(false)}
				title='Duplicate NPS Survey'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => setIsDuplicateModalOpen(false),
						children: 'Cancel',
					},
					{
						variant: 'primary',
						onClick: handleDuplicate,
						children: 'Duplicate',
					},
				]}>
				<Text variant='small'>
					Are you sure you want to duplicate <strong>"{selectedConfig?.name}"</strong>?
				</Text>
			</Modal>

			{/* Delete Modal */}
			<Modal
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				title='Delete NPS Survey'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => setIsDeleteModalOpen(false),
						children: 'Cancel',
					},
					{
						variant: 'destructive',
						onClick: handleDelete,
						children: 'Delete',
					},
				]}>
				<Text variant='small'>
					Are you sure you want to delete <strong>"{selectedConfig?.name}"</strong>? This action cannot be undone.
				</Text>
			</Modal>
		</main>
	)
}
